import { m, LazyMotion, domAnimation, Variants, useAnimation } from 'framer-motion'
import { forwardRef, useCallback, useState } from 'react'
import { ExplosionOne, ExplosionTwo } from '@icons'

import styles from './styles.module.scss'
import UniqueIngredientsCard from './unique-ingredients-card'

const MotionExplosionOne = m(ExplosionOne)
const MotionExplosionTwo = m(ExplosionTwo)

const EXPLOSION_ONE_VARIANT: Variants = {
  hidden: { scale: 2, opacity: 0.01, transition: { duration: 2 } },
  visible: { scale: 3, opacity: 1, transition: { duration: 2 } },
  end: {
    opacity: 2,
    scale: [3, 5, 40],
    transition: { duration: 2, times: [0, 0.75, 1] },
  },
}

const EXPLOSION_TWO_VARIANT: Variants = {
  hidden: { scale: 2, opacity: 0.01, transition: { duration: 2 } },
  visible: { scale: 4, opacity: 1, transition: { duration: 2 } },
  end: {
    opacity: 2,
    scale: [4, 5, 30],
    transition: { duration: 2, times: [0, 0.75, 1] },
  },
}

const CONTENT_VARIANT: Variants = {
  hidden: { y: '100%', opacity: 0 },
  middle: {
    y: '30%',
    opacity: 1,
    transition: { type: 'spring', duration: 2 },
  },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', delay: 1.3, duration: 1 },
  },
}

const INGREDIENTS_VARIANT: Variants = {
  hidden: { opacity: 0 },
  end: {
    opacity: 1,
    transition: { type: 'spring', bounce: 0.25, delay: 1.3, duration: 1 },
  },
}

export const RocketScienceSection = forwardRef<HTMLElement>((_, ref) => {
  const headingControl = useAnimation()
  const explosionControl = useAnimation()
  const explosionTwoControl = useAnimation()
  const ingredientsControl = useAnimation()
  const [wasAnimationTriggered, setWasAnimationTriggered] = useState(false)

  const onViewportEnter = useCallback(() => {
    if (wasAnimationTriggered) {
      return
    }
    setWasAnimationTriggered(true)
    explosionControl.start('visible').then(() => {
      explosionControl.start('end')
      headingControl.start('end')
      ingredientsControl.start('end')
    })

    headingControl.start('middle')

    explosionTwoControl.start('visible').then(() => {
      explosionTwoControl.start('end')
    })
  }, [
    explosionControl,
    explosionTwoControl,
    headingControl,
    ingredientsControl,
    wasAnimationTriggered,
  ])

  return (
    <section className={styles['explosion-section-container']} ref={ref}>
      <LazyMotion features={domAnimation} strict>
        <m.div
          className={styles['explosion-container']}
          viewport={{ once: true, amount: 0.3 }}
          initial="hidden"
          onViewportEnter={onViewportEnter}
        >
          <MotionExplosionOne
            className={styles['explosion-icon']}
            variants={EXPLOSION_ONE_VARIANT}
            animate={explosionControl}
          />
          <MotionExplosionTwo
            className={styles['explosion-icon']}
            variants={EXPLOSION_TWO_VARIANT}
            animate={explosionTwoControl}
          />
        </m.div>
        <m.div
          className={styles['content']}
          variants={CONTENT_VARIANT}
          initial="hidden"
          animate={headingControl}
        >
          <h2 className={styles['heading']}>
            Rocket science made <span className={styles['simple-text']}>simple</span>
          </h2>
          <m.div
            className={styles['ingredients-container']}
            variants={INGREDIENTS_VARIANT}
            initial="hidden"
            animate={ingredientsControl}
          >
            <UniqueIngredientsCard className={styles['ingredients-content']} />
          </m.div>
        </m.div>
      </LazyMotion>
    </section>
  )
})

export default RocketScienceSection
